<template>
  <div class="min-h-screen p-4 sm:p-6 lg:p-8">
    <div class="max-w-7xl mx-auto">
      <header class="mb-8 text-center">
        <!-- START: DYNAMIC LOGO -->
        <img v-if="client && client.logoUrl" :src="client.logoUrl" :alt="client.podcastName" class="h-16 w-auto inline-block">
        <img v-else class="h-16 w-auto inline-block" src="@/assets/logo.png" alt="Castive Logo">
        <!-- END: DYNAMIC LOGO -->
      </header>
      <main class="max-w-4xl mx-auto">
        <div v-if="loading" class="text-center text-gray-500 bg-white rounded-xl shadow-lg p-8"><p>Loading guide...</p></div>
        <div v-else-if="error" class="bg-white rounded-xl shadow-lg p-8 text-center"><h2 class="text-2xl font-bold text-red-700">Error</h2><p class="mt-2 text-gray-600">{{ error }}</p></div>
        <div v-else class="bg-white shadow-md rounded-xl p-6 sm:p-8">
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 text-center">Welcome, Podcast Guest!</h1>
          <p class="text-center text-gray-600 mb-6">Just a couple of quick steps before we record.</p>
          <div class="mb-8 border-t pt-6">
            <p class="text-sm text-center text-gray-500 mb-3">First, the important bit. Please sign our release agreement:</p>
            <!-- START: DYNAMIC BUTTON COLOUR -->
            <router-link 
              :to="releaseFormLink" 
              :style="{ backgroundColor: client.brandColour || '#E7643B' }"
              class="flex items-center justify-center space-x-3 p-4 hover:opacity-90 text-white rounded-lg transition-colors shadow-md">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
              <span class="font-semibold text-lg">Sign Your Release Agreement</span>
            </router-link>
            <!-- END: DYNAMIC BUTTON COLOUR -->
          </div>
          <div class="space-y-4 text-gray-700">
            <h2 class="text-xl font-semibold text-gray-800 border-b pb-2">✅ Optional: Pre-Recording Checklist</h2>
            <div class="flex items-start space-x-4">
              <span class="text-2xl mt-1">🔕</span>
              <div><h3 class="font-semibold">Turn off notifications</h3><p class="text-gray-600">Silence your phone, email, and messaging apps so there are no distractions.</p></div>
            </div>
            <div class="flex items-start space-x-4">
              <span class="text-2xl mt-1">🎤</span>
              <div><h3 class="font-semibold">Use a microphone if possible</h3><p class="text-gray-600">A USB mic or headset will make your voice much clearer.</p></div>
            </div>
            <div class="flex items-start space-x-4">
              <span class="text-2xl mt-1">🎧</span>
              <div><h3 class="font-semibold">Wear earphones or headphones</h3><p class="text-gray-600">This avoids echo and feedback during the conversation.</p></div>
            </div>
            <div class="flex items-start space-x-4">
              <span class="text-2xl mt-1">💻</span>
              <div><h3 class="font-semibold">Join on a PC/laptop instead of a mobile phone</h3><p class="text-gray-600">It’s more stable and allows for better quality video and audio.</p></div>
            </div>
            <div class="flex items-start space-x-4">
              <span class="text-2xl mt-1">🤫</span>
              <div><h3 class="font-semibold">Pick a quiet, non-echoing room</h3><p class="text-gray-600">Soft furnishings (curtains, carpet, etc.) help reduce echo.</p></div>
            </div>
          </div>
          <div class="mt-8 text-center p-4 bg-gray-50 rounded-lg">
            <p class="text-gray-800">If you’ve never done a podcast before, don’t worry: it’s just a relaxed conversation.</p>
            <p class="font-semibold text-brand-purple-dark mt-2">We can’t wait to chat with you!</p>
          </div>
        </div>
      </main>
      <footer class="mt-12 text-center py-4">
        <div class="max-w-7xl mx-auto flex items-center justify-center space-x-2">
          <img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75">
          <p class="text-sm text-gray-600 font-medium">Powered by Castive</p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default {
  name: 'PodcastGuestGuide',
  data() {
    return {
      client: null,
      loading: true,
      error: null,
    }
  },
  computed: {
    releaseFormLink() {
      const submissionId = this.$route.params.id;
      return `/guest-release-form/${submissionId}`;
    }
  },
  async created() {
    try {
      const submissionId = this.$route.params.id;
      if (!submissionId) throw new Error("No submission ID provided.");

      const submissionRef = doc(db, 'submissions', submissionId);
      const submissionSnap = await getDoc(submissionRef);
      if (!submissionSnap.exists()) throw new Error("This invitation link is invalid or has expired.");
      
      const submission = submissionSnap.data();
      if (!submission.clientId) throw new Error("Invitation is not linked to a client.");

      const clientRef = doc(db, 'clients', submission.clientId);
      const clientSnap = await getDoc(clientRef);
      if (!clientSnap.exists()) throw new Error("Could not load the associated podcast details.");
      
      this.client = clientSnap.data();
    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
    }
  }
}
</script>